/* ALEVA QA season recommendations from actual fragrance notes.
   No customer or product records are modified; unknown products remain unclassified. */
(() => {
  'use strict';
  const SEASONS = ['spring', 'summer', 'autumn', 'winter'];
  const MAX_PAGE = 24;
  const LANG = {
    de: {count:(n)=>n+' saisonale Duftvorschläge',empty:'Keine passenden Duftvorschläge gefunden.',page:(p,total)=>'Seite '+p+' von '+total,range:(a,b,t)=>'Zeige '+a+'–'+b+' von '+t,add:'Zu Favoriten hinzufügen'},
    en: {count:(n)=>n+' seasonal fragrance suggestions',empty:'No matching fragrance suggestions found.',page:(p,total)=>'Page '+p+' of '+total,range:(a,b,t)=>'Showing '+a+'–'+b+' of '+t,add:'Add to favorites'},
    ar: {count:(n)=>n+' اقتراح عطر حسب الموسم',empty:'لا توجد اقتراحات مطابقة.',page:(p,total)=>'صفحة '+p+' من '+total,range:(a,b,t)=>'عرض '+a+'–'+b+' من '+t,add:'إضافة إلى المفضلة'}
  };
  const safeSeason = (value) => SEASONS.includes(value) ? value : '';
  const norm = (value) => String(value || '').toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g,'')
    .replace(/ß/g,'ss').replace(/æ/g,'ae');
  /* Seasonal editorial suggestions: score distinct notes by pyramid position.
     A single note can contribute at most once to each season. The best
     season is returned; a second season needs comparable strong evidence.
     Product-approved season tags override the provisional scoring. */
  const NOTE_RULES = {
    spring: [['jasmin',2],['pfingstrose',2.4],['tuberose',1.6],['rose',1.6],['veilchen',1.8],['iris',1.7],['neroli',2],['orangenblute',2],['blute',0.9],['blume',0.9],['maiglockchen',2.4],['magnolie',2],['freesie',2],['lavendel',1.1],['lilie',1.8],['gardenie',2],['geranie',1.1],['mimose',1.6],['lotus',2],['orchidee',1.6],['osmanthus',1.5],['hyazinthe',2],['heliotrop',1.4],['glockenblume',2.1],['flieder',2]],
    summer: [['bergamotte',1.8],['grapefruit',2.4],['zitrone',2.2],['limette',2.3],['mandarine',1.8],['mandarin',1.8],['yuzu',2.3],['meeresnoten',2.6],['meersalz',2.5],['aquatisch',2.5],['ozean',2.4],['marine',2.2],['wasser',1.6],['minze',2.4],['kokos',1.9],['mango',2],['ananas',2],['passionsfrucht',2.3],['melone',2],['gurke',1.8],['apfel',0.8],['orange',1.1],['zitrus',2],['frisch',1.1],['grune noten',1.8]],
    autumn: [['zimt',2.2],['kardamom',1.8],['muskat',1.7],['pfeffer',1.4],['ingwer',1.2],['safran',1.8],['gewurz',1.5],['piment',1.9],['nelke',1.6],['sandelholz',1.5],['zedernholz',1.2],['patchouli',1.3],['vetiver',1.1],['guajakholz',1.4],['leder',2],['kaffee',1.5],['kakao',1.4],['rauch',1.3],['whisky',1.5],['amber',0.5],['holz',0.5]],
    winter: [['oud',2.9],['vanille',2.2],['tonkabohne',2.3],['tonka',2],['tabak',2.6],['weihrauch',2.5],['myrrhe',2.3],['praline',2.3],['karamell',2.4],['benzoe',2.2],['benzoin',2.2],['labdanum',2.2],['honig',1.5],['kakao',1.7],['kaffee',1.4],['schokolade',2.5],['dattel',1.7],['leder',1],['amber',0.55],['moschus',0.45],['harz',1.7],['balsam',1.8],['suss',0.4]]
  };
  function weightedNotes(value, season) {
    const parts=String(value||'').split(/[·,;]+/).map(v=>norm(v).trim()).filter(Boolean);
    const weights=[];
    for(const part of parts) {
      const matches=NOTE_RULES[season].filter(([term])=>{
        if(term==='orange')return /\borange\b|orangenschale|susse orange/.test(part);
        if(term==='rose')return /\brose\b|\brosen|rosen|pfingstrose|tuberose|damaszener.rose/.test(part);
        if(term==='holz')return /\bholz\b|\bholzige\b|\bholzig\b/.test(part);
        if(term==='rauch')return /\brauch\b|\brauchige\b|\brauchig\b|weihrauch/.test(part);
        return part.includes(term);
      });
      if(matches.length)weights.push(Math.max(...matches.map(m=>m[1])));
    }
    return weights;
  }
  function seasonsFor(product) {
    const tags=norm(product.tags);
    const curated=SEASONS.filter(s=>tags.includes('aleva-season-'+s));
    if(curated.length)return curated.slice(0,2);
    const notes=[product.topNotes||'',product.heartNotes||'',product.baseNotes||''];
    if(!notes.join('').trim())return [];
    const ranked=SEASONS.map(season=>{
      let score=0,cues=0;
      for(const [i,part] of notes.entries()){
        const weights=weightedNotes(part,season);
        cues+=weights.length;
        score+=weights.reduce((sum,w)=>sum+w,0)*[1.25,1.2,1.35][i];
      }
      return {season,score,cues};
    }).sort((a,b)=>b.score-a.score);
    if(ranked[0].score<3.4||ranked[0].cues<2)return [];
    const selected=[ranked[0].season];
    const second=ranked[1];
    if(second.cues>=2 && second.score>=5.3 && second.score>=ranked[0].score*0.88)selected.push(second.season);
    return selected;
  }
  function textMatch(product, query) {
    const q = norm(query).trim();
    if (!q || q === '*') return true;
    if (q.startsWith('vendor:')) {
      const name = q.slice(7).replace(/^["']|["']$/g,'');
      return norm(product.vendor) === name;
    }
    const hay = norm([product.title,product.vendor,product.tags,product.notes].join(' '));
    return q.replace(/^["']|["']$/g,'').split(/\s+/).every(word => hay.includes(word));
  }
  function genderMatch(product, selected) {
    if (!selected) return true;
    const value = norm([product.type,product.tags,product.gender].join(' '));
    if (selected === 'Herrenduft') return value.includes('herrenduft') || value.includes('herren');
    if (selected === 'Damenduft') return value.includes('damenduft') || value.includes('damen');
    return value.includes('unisex');
  }
  function makeCard(p, language) {
    const article = document.createElement('article');
    article.className='aleva-season-card';
    const imageLink=document.createElement('a');
    imageLink.className='aleva-season-card__image';
    imageLink.href=p.url;
    imageLink.setAttribute('aria-label',p.title);
    if (p.image) {
      const img=document.createElement('img');
      img.src=p.image;img.alt=p.title;img.loading='lazy';img.decoding='async';
      img.width=550;img.height=688;
      imageLink.append(img);
    } else { imageLink.textContent='ALEVA'; }
    article.append(imageLink);
    const button=document.createElement('button');
    button.type='button';
    button.className='aleva-wishlist-card-button';
    button.dataset.alevaWishlistButton='';
    button.dataset.alevaWishlistId=String(p.id);
    button.setAttribute('aria-pressed','false');
    button.setAttribute('aria-label',LANG[language].add);
    button.innerHTML='<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 20.2l7.8-6.8 1-1a5.5 5.5 0 0 0 0-7.8Z"></path></svg>';
    article.append(button);
    const info=document.createElement('div');info.className='aleva-season-card__info';
    const vendor=document.createElement('span');vendor.className='aleva-season-card__brand';vendor.textContent=p.vendor || 'ALEVA';info.append(vendor);
    const link=document.createElement('a');link.className='aleva-season-card__title';link.textContent=p.title;link.href=p.url;info.append(link);
    const price=document.createElement('span');price.className='aleva-season-card__price';price.textContent=p.priceLabel;info.append(price);
    article.append(info);
    return article;
  }
  function init() {
    const form=document.querySelector('[data-aleva-filter-form]');
    const section=form?.closest('.aleva-search-page');
    const catalogEl=section?.querySelector('[data-aleva-season-catalog]');
    const mode=section?.querySelector('[data-aleva-season-mode]');
    if(!form||!section||!catalogEl||!mode) return;
    const seasonSelect=form.querySelector('[data-aleva-season-filter]');
    const genderSelect=form.querySelector('[data-aleva-gender-filter]');
    const baseInput=form.querySelector('[data-aleva-base-query]');
    const sortSelect=form.querySelector('select[name="sort_by"]');
    const langKey=(document.documentElement.lang||'de').slice(0,2).toLowerCase();
    const lang=LANG[langKey]?langKey:'de';
    const params=new URL(window.location.href).searchParams;
    const initial=safeSeason(params.get('aleva_season'));
    const savedGender=params.get('aleva_gender');
    if(initial) {
      seasonSelect.value=initial;
      seasonSelect.dispatchEvent(new Event('change',{bubbles:true}));
      if(['Herrenduft','Damenduft','Unisex'].includes(savedGender)){
        genderSelect.value=savedGender;
        genderSelect.dispatchEvent(new Event('change',{bubbles:true}));
      }
    }
    form.addEventListener('submit', (event) => {
      const season=safeSeason(seasonSelect.value);
      if(!season) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const url=new URL(form.action, window.location.href);
      const base=baseInput.getAttribute('value')?.trim()||'*';
      url.searchParams.set('q',base);
      url.searchParams.set('type','product');
      url.searchParams.set('options[prefix]','last');
      url.searchParams.set('aleva_season',season);
      const previewId=params.get('preview_theme_id');
      if(/^\d{10,16}$/.test(previewId||''))url.searchParams.set('preview_theme_id',previewId);
      if(genderSelect.value)url.searchParams.set('aleva_gender',genderSelect.value);
      if(sortSelect.value && sortSelect.value!=='relevance')url.searchParams.set('sort_by',sortSelect.value);
      window.location.assign(url.href);
    },true);
    /* Search submissions should not lose the QA preview. */
    const previewId=params.get('preview_theme_id');
    const searchForm=section.querySelector('.aleva-search-page__form');
    if(searchForm && /^\d{10,16}$/.test(previewId||'')){
      searchForm.addEventListener('submit',event=>{
        event.preventDefault();
        const url=new URL(searchForm.action,window.location.href);
        url.searchParams.set('q',searchForm.querySelector('input[name="q"]')?.value||'');
        url.searchParams.set('options[prefix]','last');
        url.searchParams.set('preview_theme_id',previewId);
        window.location.assign(url.href);
      });
    }
    if(!initial)return;
    let products=[];
    try {
      const raw=JSON.parse(catalogEl.textContent);
      products=Array.isArray(raw)?raw:[];
    } catch(_) { products=[]; }
    const base=baseInput.getAttribute('value')||'*';
    const gender=genderSelect.value||'';
    let matched=products.filter(p=>p&&typeof p==='object'&&
      /^\d{6,20}$/.test(String(p.id))&&typeof p.url==='string'&&
      p.url.startsWith('/')&&typeof p.title==='string'&&
      seasonsFor(p).includes(initial)&&genderMatch(p,gender)&&textMatch(p,base));
    if(sortSelect.value==='price-ascending') matched.sort((a,b)=>Number(a.price)-Number(b.price));
    else if(sortSelect.value==='price-descending') matched.sort((a,b)=>Number(b.price)-Number(a.price));
    else matched.sort((a,b)=>a.title.localeCompare(b.title,lang));
    section.querySelectorAll('.aleva-search-page__summary,.aleva-search-page__results,.aleva-search-page__pagination,.aleva-search-page__empty').forEach(el=>{
      el.hidden=true; el.setAttribute('aria-hidden','true');
    });
    mode.hidden=false;
    const grid=mode.querySelector('[data-aleva-season-grid]');
    const status=mode.querySelector('[data-aleva-season-count]');
    const bottomPager=mode.querySelector('[data-aleva-season-pager]');
    const topPager=bottomPager.cloneNode(true);
    topPager.classList.add('aleva-season-mode__pager--top');
    topPager.removeAttribute('data-aleva-season-pager');
    topPager.setAttribute('data-aleva-season-pager-top','');
    grid.before(topPager);
    const pagers=[topPager,bottomPager];
    const totalPages=Math.ceil(matched.length/MAX_PAGE);
    function readPage(){
      const value=new URL(window.location.href).searchParams.get('aleva_page');
      const n=value && /^\d{1,5}$/.test(value)?Number(value):1;
      return Math.min(Math.max(n,1),Math.max(1,totalPages));
    }
    let page=readPage();
    function visiblePages(){
      if(totalPages<=7) return Array.from({length:totalPages},(_,i)=>i+1);
      const set=new Set([1,totalPages]);
      for(let i=Math.max(1,page-2);i<=Math.min(totalPages,page+2);i++)set.add(i);
      const ordered=[...set].sort((a,b)=>a-b);
      const shown=[];
      ordered.forEach((n,i)=>{
        if(i && n-ordered[i-1]>1)shown.push(null);
        shown.push(n);
      });
      return shown;
    }
    function navigateTo(n,writeUrl=true){
      if(!Number.isInteger(n)||n<1||n>totalPages||n===page)return;
      page=n;
      if(writeUrl){
        const url=new URL(window.location.href);
        if(n===1)url.searchParams.delete('aleva_page');
        else url.searchParams.set('aleva_page',String(n));
        window.history.pushState(null,'',url.href);
      }
      render();
      mode.scrollIntoView({block:'start',behavior:'instant'});
    }
    function render(){
      grid.replaceChildren();
      const fragment=document.createDocumentFragment();
      matched.slice((page-1)*MAX_PAGE,page*MAX_PAGE).forEach(p=>fragment.append(makeCard(p,lang)));
      grid.append(fragment);
      const first=matched.length?(page-1)*MAX_PAGE+1:0;
      const last=Math.min(page*MAX_PAGE,matched.length);
      status.textContent=matched.length
        ?LANG[lang].count(matched.length)+' · '+LANG[lang].range(first,last,matched.length)
        :LANG[lang].empty;
      pagers.forEach(pager=>{
        pager.hidden=totalPages<2;
        if(totalPages<2)return;
        const prev=pager.querySelector('[data-aleva-season-prev]');
        const next=pager.querySelector('[data-aleva-season-next]');
        const pageLabel=pager.querySelector('[data-aleva-season-page]');
        pageLabel.textContent=LANG[lang].page(page,totalPages);
        prev.disabled=page===1;
        next.disabled=page===totalPages;
        let numbers=pager.querySelector('[data-aleva-season-numbers]');
        if(!numbers){
          numbers=document.createElement('span');
          numbers.className='aleva-season-mode__numbers';
          numbers.dataset.alevaSeasonNumbers='';
          pageLabel.before(numbers);
        }
        numbers.replaceChildren();
        visiblePages().forEach(n=>{
          if(n===null){
            const dots=document.createElement('span');
            dots.className='aleva-season-mode__ellipsis';
            dots.textContent='…';
            dots.setAttribute('aria-hidden','true');
            numbers.append(dots);
            return;
          }
          const button=document.createElement('button');
          button.type='button';
          button.textContent=String(n);
          button.setAttribute('aria-label',LANG[lang].page(n,totalPages));
          if(n===page)button.setAttribute('aria-current','page');
          button.addEventListener('click',()=>navigateTo(n));
          numbers.append(button);
        });
        prev.onclick=()=>navigateTo(page-1);
        next.onclick=()=>navigateTo(page+1);
      });
      /* Keep saved wishlist hearts visually consistent across pages. */
      let saved=[];
      try {const ids=JSON.parse(localStorage.getItem('aleva-wishlist-products')||'[]');saved=Array.isArray(ids)?ids.map(String):[];}catch(_){}
      for(const b of grid.querySelectorAll('[data-aleva-wishlist-button]')){
        const active=saved.includes(b.dataset.alevaWishlistId);
        b.classList.toggle('is-active',active);
        b.setAttribute('aria-pressed',String(active));
      }
    }
    window.addEventListener('popstate',()=>{
      const n=readPage();
      if(n!==page){page=n;render();}
    });
    render();
  }
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',init,{once:true});
  else init();
})();